import { WalletKey, WalletOption } from "@silo/types";
import { cn } from "../../utils/class-names";
import { WalletDisplay } from "../wallet-display";
import { CheckmarkIcon } from "../../icons";
import { isNotNullable } from "effect/Predicate";
import { buttonV2 } from "../button-v2";
import { DialogButton } from "./dialog-button";
import { useBreakpoints } from "../../hooks/use-breakpoints";

export interface SelectWalletProps {
  className?: string;
  walletOptions: WalletOption[];
  isPending: boolean;
  activeWalletKey: WalletKey | null;
  selectWallet: (value: WalletKey) => void;
}

export function SelectWallet({
  className,
  walletOptions,
  isPending,
  activeWalletKey,
  selectWallet,
}: SelectWalletProps): JSX.Element {
  return (
    <div className={cn("flex flex-col gap-10", className)}>
      <h2 className="text-xl font-medium text-white">Select a wallet</h2>

      <div className="flex flex-col items-stretch gap-5 sm:items-start">
        {walletOptions.map(({ walletKey, href }) => (
          <WalletOptionDisplay
            isPending={isPending}
            walletKey={walletKey}
            activeWalletKey={activeWalletKey}
            href={href}
            setWalletKey={selectWallet}
          />
        ))}
      </div>
    </div>
  );
}

interface WalletOptionDisplayProps {
  isPending: boolean;
  walletKey: WalletKey;
  activeWalletKey: WalletKey | null;
  href: string | null;
  setWalletKey: (value: WalletKey) => void;
}

function WalletOptionDisplay({
  isPending,
  walletKey,
  activeWalletKey,
  href,
  setWalletKey,
}: WalletOptionDisplayProps): JSX.Element {
  const { md } = useBreakpoints();

  const isSelected = activeWalletKey
    ? walletKey === activeWalletKey
    : undefined;

  const content = (
    <div className="flex items-center justify-between gap-5">
      <WalletDisplay walletKey={walletKey} />

      {isSelected && <CheckmarkIcon className="w-6 h-6" />}
    </div>
  );

  if (isPending) {
    return (
      <DialogButton isSelected={isSelected} disabled>
        {content}
      </DialogButton>
    );
  }

  if (isNotNullable(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={buttonV2({
          variant: md ? "tertiary" : "secondary",
          isSelected,
        })}
      >
        {content}
      </a>
    );
  }

  return (
    <DialogButton
      isSelected={isSelected}
      onClick={() => setWalletKey(walletKey)}
    >
      {content}
    </DialogButton>
  );
}
